import {
  Color,
  GLSL3,
  Mesh,
  OrthographicCamera,
  PlaneGeometry,
  Scene,
  ShaderMaterial,
  Vector2,
  type WebGLRenderer,
} from "three";

// シェーダー内のメタボール最大点数。中心 cube (24) + kaleidoscope コピー
// (rings=1 で 6×24=144) を合わせて 168。余裕をみて 256。動的ループ (uCount)
// なので、実際に使う点数だけループする (中心のみなら 24)。
const MAX_POINTS = 256;

/**
 * cube の頂点を画面空間のメタボール (逆距離場の和 → しきい値) として描画する
 * フルスクリーン・オーバーレイ。参考記事の 2D メタボールと同方式で、各頂点を
 * `strength / distance` の場のソースにし、合計をしきい値処理して blob を作る。
 *
 * 使い方:
 *   - setResolution / setColor / setStrength / setThreshold で見た目を設定
 *   - 毎フレーム setPoint(i, x, y, weight) で各頂点の画面座標 (gl_FragCoord 系の
 *     物理ピクセル、y 上向き) と重み (occlusion フェード) を渡す
 *   - render(renderer) でメインシーンの上に合成描画 (autoClear を一時 false に)
 *
 * 軽量なので rotate / kaleidoscope と同様トグルで ON/OFF する想定。
 */
export class MetaballOverlay {
  private readonly scene: Scene;
  private readonly camera: OrthographicCamera;
  private readonly material: ShaderMaterial;
  private readonly geometry: PlaneGeometry;

  constructor() {
    this.scene = new Scene();
    // フルスクリーン quad 用。vertex shader が NDC をそのまま出すので
    // カメラ行列は使わないが、render に必要なので置いておく。
    this.camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const pointVecs: Vector2[] = [];
    for (let i = 0; i < MAX_POINTS; i++) pointVecs.push(new Vector2(-1e4, -1e4));

    this.material = new ShaderMaterial({
      glslVersion: GLSL3,
      uniforms: {
        uResolution: { value: new Vector2(1, 1) },
        uCount: { value: 0 },
        uPoints: { value: pointVecs },
        uWeights: { value: new Array(MAX_POINTS).fill(0) as number[] },
        uThreshold: { value: 0.7 },
        uStrength: { value: 0.2 },
        // エッジの柔らかさ。0 = 記事と同じハードな step (完全な 0/1)、
        // 大きいほど smoothstep でフェード。
        uSmoothness: { value: 0.0 },
        uColor: { value: new Color(0xa67c52) },
      },
      vertexShader: /* glsl */ `
        void main() {
          // PlaneGeometry(2,2) の position.xy は [-1,1] = NDC をそのまま使う。
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform vec2 uResolution;
        uniform int uCount;
        uniform vec2 uPoints[${MAX_POINTS}];
        uniform float uWeights[${MAX_POINTS}];
        uniform float uThreshold;
        uniform float uStrength;
        uniform float uSmoothness;
        uniform vec3 uColor;
        out vec4 fragColor;

        void main() {
          // 記事と同じ正規化: 短辺基準で [-1,1] 付近へ。
          float minRes = min(uResolution.x, uResolution.y);
          vec2 p = (gl_FragCoord.xy * 2.0 - uResolution) / minRes;
          float c = 0.0;
          // 動的ループ: 実際に投入された点数 (uCount) だけ評価する。
          for (int i = 0; i < uCount; i++) {
            vec2 pi = (uPoints[i] * 2.0 - uResolution) / minRes;
            float d = max(length(p - pi), 0.0001);
            c += uStrength * uWeights[i] / d;
          }
          // uSmoothness=0 で記事と同じ step (完全な 0/1)、>0 で smoothstep フェード。
          float m;
          if (uSmoothness < 0.0005) {
            m = step(uThreshold, c);
          } else {
            m = smoothstep(uThreshold - uSmoothness, uThreshold + uSmoothness, c);
          }
          if (m < 0.002) discard;
          fragColor = vec4(uColor, m);
        }
      `,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    this.geometry = new PlaneGeometry(2, 2);
    const quad = new Mesh(this.geometry, this.material);
    quad.frustumCulled = false;
    this.scene.add(quad);
  }

  /** 投入できる最大点数。呼び出し側はこれを超えないように clamp する。 */
  get maxPoints(): number {
    return MAX_POINTS;
  }

  /** このフレームで実際に評価する点数 (動的ループの上限)。 */
  setCount(n: number): void {
    this.material.uniforms.uCount.value = Math.max(0, Math.min(MAX_POINTS, n));
  }

  setResolution(w: number, h: number): void {
    (this.material.uniforms.uResolution.value as Vector2).set(w, h);
  }

  setThreshold(v: number): void {
    this.material.uniforms.uThreshold.value = v;
  }

  /** エッジの柔らかさ (0 = ハード step / 完全な 0/1、>0 で smoothstep フェード)。 */
  setSmoothness(v: number): void {
    this.material.uniforms.uSmoothness.value = v;
  }

  setStrength(v: number): void {
    this.material.uniforms.uStrength.value = v;
  }

  setColor(color: string): void {
    (this.material.uniforms.uColor.value as Color).set(color);
  }

  /**
   * 頂点 i の画面座標 (gl_FragCoord 系、物理ピクセル、y 上向き) と重みを設定。
   * weight は occlusion フェード等 (0 で寄与なし)。
   */
  setPoint(i: number, x: number, y: number, weight: number): void {
    if (i < 0 || i >= MAX_POINTS) return;
    (this.material.uniforms.uPoints.value as Vector2[])[i].set(x, y);
    (this.material.uniforms.uWeights.value as number[])[i] = weight;
  }

  /** メインシーンの上に合成描画する (画面をクリアしない)。 */
  render(renderer: WebGLRenderer): void {
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.render(this.scene, this.camera);
    renderer.autoClear = prevAutoClear;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
